import {AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {carouselEnum} from '@app/components/carousel/iCarousel';
import {
  BrowserStorageService,
  CacheObjectService,
  DeviceService,
  EventTrackerService,
  LogService,
  TypeShowLogInConsole,
} from '@app/core';
import {ScrollToLastPosition} from '@app/helper/scroll-to-last-position';
import {iBook} from '@app/interfaces/iBook';
import {IndicatorService} from '@app/shared';
import {catchError, distinctUntilChanged, filter, finalize, map, shareReplay, switchMap, tap} from 'rxjs/operators';

import {SearchItemResponse} from './isearch';
import {SearchService} from './search.service';
import {Subject, Subscription} from "rxjs";

@Component({
  selector: 'navaar-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})

export class SearchComponent extends ScrollToLastPosition implements OnInit, OnDestroy {
  @HostListener('keydown', ['$event'])
  onkeydown(event: KeyboardEvent) {
    if (event.keyCode === 229 && event.isComposing) {
      event.preventDefault();
    }
  }

  @ViewChild('search_input', {read: ElementRef, static: true}) private searchInput: ElementRef<HTMLInputElement>;
  public carousel: typeof carouselEnum = carouselEnum;
  public wordSearch: string = "";
  public isEnd: boolean = false;
  private pageSize: number = 25;
  public isLoading: boolean = false;
  public isEmptyResultSearch: boolean = false;
  public items: Array<SearchItemResponse> = [];
  public itemsForEmptySearch: any = null;
  public itemsSearched: Set<string> = new Set();
  private readonly ITEM_SEARCH: string = "search_item";

  public get isBackFromItem(): boolean {
    return this.searchService.isBackFromItem;
  }

  public set isBackFromItem(v: boolean) {
    this.searchService.isBackFromItem = v;
  }

  public get page(): number {
    return this.cache.pageNumer;
  }

  public set page(v: number) {
    this.cache.pageNumer = v;
  }

  private subscription$: Subscription = new Subscription();
  private getPharmacies$: Subject<Array<SearchItemResponse>> = new Subject<Array<SearchItemResponse>>();

  constructor(
    public readonly device: DeviceService,
    public readonly storage: BrowserStorageService,
    private readonly router: Router,
    private readonly searchService: SearchService,
    private readonly route: ActivatedRoute,
    private readonly cache: CacheObjectService<Array<iBook>>,
    private readonly log: LogService,
    private readonly indicator: IndicatorService,
    private readonly eventTracker: EventTrackerService) {
    super(router);
  }

  public async ngOnInit() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.restoredState)
          this.isBackFromItem = true;
      });
    const searchedItem: Array<string> = this.storage.getLocal(this.ITEM_SEARCH) ?? [];
    if (searchedItem.length > 0) {
      this.itemsSearched = new Set(searchedItem);
    }
    this.init();
    this.indicator.hide();
    this.log.logger('search component ngOnInit START', TypeShowLogInConsole.info);
    this.wordSearch = this.route.snapshot.queryParamMap.get('q');
    if (this.wordSearch) this.getPharmacies$.next();
  }

  private init() {
    this.subscription$.add(
      this.getPharmacies$
        .pipe(
          tap(c => {
            this.indicator.showHude();
            this.isLoading = true;
            this.isEmptyResultSearch = false;
          }),
          switchMap((z) => {
            return this.searchService.list(this.wordSearch.trim(), this.page, this.pageSize);
          }),
          shareReplay(1),
          finalize(() => this.indicator.hideHude()),
          map((res) => {
            if (!this.isBackFromItem)
              this.eventTracker.logEvent('search', {search_term: this.wordSearch, x_search_result_count: res.total});
            if (res.total === 0) {
              this.items = [];
              this.getItemsSlider();
              this.isEmptyResultSearch = true;
              return;
            }
            this.isEnd = (res.items.length === res.total);
            this.items = res.items;
            this.indicator.hideHude();
            this.isLoading = false;
            if (!this.route.snapshot.queryParamMap.has('q')) {
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {q: this.wordSearch},
                queryParamsHandling: 'merge',
                replaceUrl: true
              }).then();
            }
          }),
          catchError((err) => {
            this.isLoading = false;
            throw err;
          }),
          distinctUntilChanged(),
        )
        .subscribe({
          next: (x) => {
            if (this.isBackFromItem) {
              super.scrollToPosition();
              this.isBackFromItem = false;
            }
          },
          error: () => {
            this.init();
          },
        }),
    );
  }

  public async goToNextPage(page: number) {
    this.isLoading = true;
    this.page = page;
    this.getPharmacies$.next();
  }

  public onSearch(event: KeyboardEvent) {
    event.preventDefault();
    if (event.code === 'enter' || event.key === 'enter' || event.key === 'Enter' || event.which === 13 || event.keyCode === 13)
      this.search();
  }

  public search() {
    this.indicator.showHude();
    this.itemsForEmptySearch = null;
    this.page = 0;
    this.isEnd = false;
    this.isBackFromItem = false;
    this.searchInput.nativeElement.blur();
    if (this.wordSearch?.length > 0) {
      this.getPharmacies$.next();
      if (!this.itemsSearched.has(this.wordSearch)) {
        if (this.itemsSearched.size >= 5) {
          this.itemsSearched.delete(this.itemsSearched.values().next().value);
        }
        this.itemsSearched.add(this.wordSearch);
        this.storage.setLocal(this.ITEM_SEARCH, [...this.itemsSearched]);
        this.itemsSearched = new Set(this.itemsSearched);
      }
    } else {
      this.items = [];
      this.router.navigate(['/search'], {queryParams: null, replaceUrl: true}).then();
      this.indicator.hideHude();
    }
  }

  public ngOnDestroy() {
    this.indicator.show();
    this.indicator.hideHude();
    this.subscription$.unsubscribe();
  }

  public getItemsSlider() {
    this.cache.getObjectCache('api/audiobooks/bestseller?$top=8', `search---${this.wordSearch}`).subscribe(c => {
      this.indicator.hideHude();
      this.isLoading = false;
      this.itemsForEmptySearch = c;
    }, () => {
      this.indicator.hideHude();
      this.isLoading = false;
    })
  }

  public searchSelected(item) {
    this.wordSearch = item;
    this.search();
  }

  public reset() {
    this.wordSearch = '';
    this.items = [];
    if (this.route.snapshot.queryParamMap.has('q')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {q: this.wordSearch},
        queryParamsHandling: 'merge',
        replaceUrl: true
      }).then();
    }
  }
}
